import React, { useMemo } from "react";
import { Button } from "@/components/ui";
import { FormField, SubscribedPlayerSearchResponse, UpdatePlayerRequest, WebSettings } from "@/interfaces";
import { useAccountSettings, useSocialCoachSession } from "@/utils";
import { SubscriptionServices } from "@/services";
import { Formik } from "formik";
import { toast } from "sonner";
import { SegmentationDropDown } from "@/containers/Coach/EditPlayer/components/SegmentationDropdown";
import { FormCard } from "@/components/FormCard";
import { PlayerSegmentationType } from "@/services/bedrock";

interface Props {
  player: SubscribedPlayerSearchResponse;
  clearCaches: () => void;
  reloadPlayer: () => void;
  withWritePermission: boolean;
}

export function PlayerSegmentationForm({ player, clearCaches, reloadPlayer, withWritePermission }: Props) {
  const playerId = player.playerAccount.playerId!;
  const { coachId } = useSocialCoachSession();
  const { hasAccountSetting } = useAccountSettings(coachId);

  const segmentationFields: FormField[] = [
    {
      id: "branchNames",
      name: "branchNames",
      type: "BRANCH",
      label: "Branches",
      placeholder: "Branch (Optional)",
      disabled: !withWritePermission,
    },
    {
      id: "regionNames",
      name: "regionNames",
      type: "REGION",
      label: "Regions",
      placeholder: "Region (Optional)",
      disabled: !withWritePermission,
    },
    {
      id: "costCenterNames",
      name: "costCenterNames",
      type: "COST_CENTER",
      label: "Cost Centers",
      placeholder: "Cost Center (Optional)",
      disabled: !withWritePermission,
    },
    {
      id: "stateCodes",
      name: "stateCodes",
      type: "STATE",
      label: "States",
      placeholder: "State (Optional)",
      disabled: !withWritePermission,
    },
    {
      id: "industryRoleNames",
      name: "industryRoleNames",
      type: "INDUSTRY_ROLE",
      label: "Industry Role",
      placeholder: "Industry Role (Optional)",
      disabled: !withWritePermission,
    },
  ];

  const displayedFields = useMemo(() => {
    return segmentationFields.filter(e => e.id !== "costCenterNames" || hasAccountSetting(WebSettings.COST_CENTER));
  }, [hasAccountSetting]);

  return (
    <Formik
      validateOnBlur={false}
      validateOnChange={false}
      initialValues={{
        branchNames: player.branchNames,
        stateCodes: player.stateCodes,
        regionNames: player.regionNames,
        costCenterNames: player.costCenterNames,
        industryRoleNames: player.industryRoleNames,
      }}
      onSubmit={async ({ branchNames, regionNames, stateCodes, costCenterNames, industryRoleNames }) => {
        try {
          const updatePlayerRequest: UpdatePlayerRequest = {
            branchNames: branchNames,
            stateCodes: stateCodes,
            regionNames: regionNames,
            costCenterNames: costCenterNames,
            industryRoleNames: industryRoleNames,
          };

          await SubscriptionServices.update(playerId, coachId!, updatePlayerRequest);
          toast.success("Player segmentation updated successfully");
          clearCaches();
          reloadPlayer();
        } catch (e) {
          console.error(e);
          toast.error("Error updating player segmentation");
        }
      }}
    >
      {({ values, handleSubmit, setFieldValue, isSubmitting }) => {
        return (
          <FormCard>
            <FormCard.Header
              title="Segmentation"
              description="Group your team to automate relevant prompts and campaigns"
            />
            <FormCard.Content>
              {displayedFields.map(field => (
                <SegmentationDropDown
                  id={field.id}
                  type={field.type as PlayerSegmentationType}
                  key={`edit_player_group_${field.id}`}
                  label={field.label}
                  placeholder={field.placeholder}
                  fieldValue={values[field.id as keyof typeof values]}
                  multiple={true}
                  disabled={isSubmitting || field.disabled}
                  allowAdditions={!["industryRoleNames", "stateCodes"].includes(field.id)}
                  handleChange={data => {
                    setFieldValue(field.id, data.value);
                  }}
                />
              ))}
            </FormCard.Content>
            <FormCard.Actions>
              <Button
                onClick={() => handleSubmit()}
                disabled={isSubmitting || !withWritePermission}
                loading={isSubmitting}
              >
                Save
              </Button>
            </FormCard.Actions>
          </FormCard>
        );
      }}
    </Formik>
  );
}
